import classNames from 'classnames';
import { TFunction } from 'i18next';
import { useEffect, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { DeliveryAddressSelection } from '@steelbuy/form';
import {
    MaterialWithDimensions,
    Literal,
    getDimensionUnit,
    getTradeUnit,
    Product,
    ThicknessOption,
    ThicknessType,
    WidthOption,
} from '@steelbuy/ts-shared';
import {
    ButtonSize,
    ButtonTertiary,
    Card,
    IconIdentifier,
    Notification,
    NotificationLevel,
} from '@steelbuy/ui-primitive';
import { useWindowSize } from '@steelbuy/util';
import { RFQInputs, isUniqueEntry, removeNullFields, mapInputs, updateNonApplicableFields } from '../../createRFQUtil';
import { useRfqFormContext } from '../../RfqFormContext';

import './AddRFQItem.scss';

interface AddRFQItemProps {
    append: (data: MaterialWithDimensions) => void;
    headings: RFQInputs;
    material: MaterialWithDimensions;
    fields: MaterialWithDimensions[];
    hasAddedRows: boolean;
    draftId: string | null;
    stepVisited: boolean;
    saveDraftCounter: number;
    onDirtyChange?: (isDirty: boolean) => void;
}

const buildMeasurementText = (
    measurementType: string | undefined,
    measurementLiteral: string | undefined,
    material: MaterialWithDimensions,
    t: TFunction
): string => {
    if (measurementType !== undefined) {
        const literal =
            measurementType === 'dimension'
                ? `domainModel:material.dimensions.units.${getDimensionUnit()}.label`
                : (`domainModel:material.tradeUnit.value.${getTradeUnit({
                      materialType: material.materialType,
                      product: material.product,
                  })}.label` as Literal);
        return ` (${t(literal)})`;
    }
    if (measurementLiteral) {
        return ` (${t(`domainModel:${measurementLiteral}` as Literal)})`;
    }
    return '';
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type RFQNumber = any;

export const AddRFQItem = ({
    append,
    material,
    headings,
    fields,
    hasAddedRows,
    draftId,
    stepVisited,
    saveDraftCounter,
    onDirtyChange,
}: AddRFQItemProps) => {
    const { t } = useTranslation('translation');
    const [error, setError] = useState('');
    const triggerOnChange = useRef(false);
    const { width } = useWindowSize();
    const [isAddButtonWrapped, setIsAddButtonWrapped] = useState(false);
    const methods = useForm<MaterialWithDimensions>({
        defaultValues: {
            grade: undefined,
            specification: undefined,
            finish: undefined,
            width: '' as RFQNumber,
            weight: '' as RFQNumber,
            thickness: '' as RFQNumber,
            thicknessMin: '' as RFQNumber,
            thicknessMax: '' as RFQNumber,
            widthMin: '' as RFQNumber,
            widthMax: '' as RFQNumber,
            widthOption:
                'widthOption' in headings && material.product === Product.COIL ? WidthOption.RANGE : WidthOption.SINGLE,
            thicknessOption:
                'thicknessOption' in headings && material.product === Product.COIL
                    ? ThicknessOption.RANGE
                    : ThicknessOption.SINGLE,
            thicknessType: material.product === Product.SHEET ? ThicknessType.NOM : undefined,
            length: '' as RFQNumber,
            quality: undefined,
            tolerance: undefined,
            surface: undefined,
            coatingThicknessValue: '' as RFQNumber,
            gradeFamily: undefined,
            coatingWeight: undefined,
            surfaceTreatment: undefined,
            compliance: undefined,
            surfaceFinish: undefined,
            deliveryAddress: undefined,
        },
        mode: 'all',
    });

    const {
        control,
        handleSubmit,
        watch,
        reset,
        formState: { isValid, errors, isDirty },
        clearErrors,
        setValue,
        trigger,
    } = methods;

    const { cancelEditRow, warehouseAddresses } = useRfqFormContext();
    const showAddressSelector = warehouseAddresses.length > 0;

    useEffect(() => {
        updateNonApplicableFields(headings, { ...material, ...watch() }, clearErrors, watch, setValue, errors);
        if (triggerOnChange.current && draftId) {
            triggerOnChange.current = false;
            trigger();
        }
    }, [watch(), draftId]);

    useEffect(() => {
        if ((stepVisited || draftId) && !hasAddedRows) {
            trigger();
        }
    }, [draftId, hasAddedRows, stepVisited, isDirty, trigger]);

    useEffect(() => {
        onDirtyChange?.(isDirty);
    }, [isDirty]);

    useEffect(() => {
        reset();
        setError('');
        if (!hasAddedRows) {
            triggerOnChange.current = true;
        }
    }, [saveDraftCounter, reset]);

    const thicknessOptionValue = watch('thicknessOption');
    const widthOptionValue = watch('widthOption');

    useEffect(() => {
        if (thicknessOptionValue === ThicknessOption.RANGE) {
            setValue('thickness', '' as RFQNumber);
            clearErrors('thickness');
        } else {
            setValue('thicknessMin', '' as RFQNumber);
            setValue('thicknessMax', '' as RFQNumber);
            clearErrors('thicknessMin');
            clearErrors('thicknessMax');
            if (material.product === Product.SHEET) {
                setValue('thicknessType', ThicknessType.NOM);
            }
        }
    }, [thicknessOptionValue]);

    useEffect(() => {
        if (widthOptionValue === WidthOption.RANGE) {
            setValue('width', '' as RFQNumber);
            clearErrors('width');
        } else {
            setValue('widthMin', '' as RFQNumber);
            setValue('widthMax', '' as RFQNumber);
            clearErrors('widthMin');
            clearErrors('widthMax');
        }
    }, [widthOptionValue]);

    const addButtonRef = useRef<HTMLDivElement>(null);
    const lastColumnGroupRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (lastColumnGroupRef.current && addButtonRef.current) {
            setIsAddButtonWrapped(
                addButtonRef.current.getBoundingClientRect().top >
                    lastColumnGroupRef.current.getBoundingClientRect().top
            );
        }
    }, [watch(), width]);

    return (
        <section className="add-rfq-item">
            <Card isClickable={false}>
                <div className="add-rfq-item__container">
                    <p className="add-rfq-item__title">{t('application.addRfq.addTitle')}</p>
                    <p className="add-rfq-item__description">{t('application.addRfq.addDescription')}</p>
                </div>
                <FormProvider {...methods}>
                    <div className="add-rfq-item__content add-rfq-item__content--wrapping">
                        {Object.entries(headings).map(
                            (
                                [
                                    key,
                                    {
                                        Component,
                                        rules,
                                        rulesMin,
                                        rulesMax,
                                        getOptions,
                                        disabledFn,
                                        translationKey,
                                        endAdornment,
                                        measurementLiteral,
                                        measurementType,
                                    },
                                ],
                                index,
                                arr
                            ) => {
                                const isDisabled = disabledFn?.({ ...material, ...watch() });
                                const measurementText = buildMeasurementText(
                                    measurementType,
                                    measurementLiteral,
                                    material,
                                    t
                                );

                                return (
                                    <div
                                        key={key}
                                        className="add-rfq-item__content__column-group"
                                        ref={
                                            index === arr.length - 1 && !showAddressSelector
                                                ? lastColumnGroupRef
                                                : undefined
                                        }
                                    >
                                        <div
                                            className={classNames('add-rfq-item__label', {
                                                'add-rfq-item__label--disabled': isDisabled,
                                            })}
                                        >
                                            <div className="add-rfq-item__label__container">
                                                <span>{t(`application.rfqHeadingLabels.${key}` as Literal)}</span>
                                                {measurementText && <span>{measurementText}</span>}
                                                {((rules?.required && !isDisabled) ||
                                                    (key === 'thicknessType' &&
                                                        watch('thicknessOption') === ThicknessOption.SINGLE)) && (
                                                    <span className="required-field"> *</span>
                                                )}
                                            </div>
                                        </div>
                                        <Component
                                            material={{ ...material, ...watch() }}
                                            control={control}
                                            name={key}
                                            rules={rules}
                                            rulesMin={rulesMin}
                                            rulesMax={rulesMax}
                                            error={errors?.[key]}
                                            errors={errors}
                                            getOptions={getOptions}
                                            translationKey={translationKey}
                                            endAdornment={endAdornment}
                                            disabled={isDisabled}
                                        />
                                    </div>
                                );
                            }
                        )}

                        {showAddressSelector && (
                            <div className="add-rfq-item__content__column-group" ref={lastColumnGroupRef}>
                                <div className="add-rfq-item__label">
                                    <div className="add-rfq-item__label__container">
                                        <span>{t('application.rfqHeadingLabels.deliveryAddress' as Literal)}</span>
                                        <span className="required-field"> *</span>
                                    </div>
                                </div>
                                <DeliveryAddressSelection
                                    name="deliveryAddress"
                                    control={control}
                                    addresses={warehouseAddresses}
                                    error={errors?.deliveryAddress}
                                    rules={{ required: true }}
                                />
                            </div>
                        )}

                        <div
                            ref={addButtonRef}
                            className={classNames('add-rfq-item__content__add-button', {
                                'add-rfq-item__content__add-button--wrapped': isAddButtonWrapped,
                            })}
                        >
                            <ButtonTertiary
                                label={t('application.createRFQ.addToRFQ')}
                                size={ButtonSize.S}
                                icon={IconIdentifier.PLUS}
                                type="submit"
                                onClick={handleSubmit(
                                    (data) => {
                                        if (isUniqueEntry(data, fields)) {
                                            cancelEditRow();
                                            setError('');
                                            let mappedValues = removeNullFields(data);
                                            mappedValues = mapInputs(mappedValues);
                                            append(mappedValues);
                                            reset();
                                        } else {
                                            setError(t('application.addRfq.itemExists'));
                                        }
                                    },
                                    () => setError(t('application.addRfq.genericError'))
                                )}
                                disabled={!isValid}
                            />
                        </div>
                    </div>
                </FormProvider>
                {!!error && <Notification level={NotificationLevel.ERROR} message={error} stayOpen disableScroll />}
            </Card>
        </section>
    );
};
